import {httpClientV2} from "../../infrastructure/http/httpClient";
import {
  OfferingCollectionBlock,
  OfferingReference,
  PublicProfileBlock,
  PublicProfileDocument,
  RichTextBlock,
} from "../domain";

type ApiResponse<T> = {status: boolean; message: string; data: T};
type ApiMutationResponse = {status: boolean; message: string};

export type ContentBlockType = "1:1_sessions" | "live_event" | "store" | "text";
export type ContentBlockRefType =
  | "BOOKING"
  | "LIVE_EVENT"
  | "PRODUCT"
  | "PRODUCT_SERVICE";

export type ContentBlockItem = {
  id: number;
  refType: ContentBlockRefType;
  refId: number;
  position: number;
  offering: {id: number; name: string; slug: string; tinyUrl: string};
};

export type ContentBlock = {
  id: number;
  userId: number;
  type: ContentBlockType;
  title: string | null;
  description: string | null;
  position: number;
  items: ContentBlockItem[];
  createdAt: string;
  updatedAt: string;
};

export type ContentBlockRequest = {
  type: ContentBlockType;
  title: string;
  description?: string | null;
  position: number;
  items?: Array<{
    refType: ContentBlockRefType;
    refId: number;
    position: number;
  }>;
};

const CONTENT_BLOCK_ID_PREFIX = "content-block:";

export function contentBlockDocumentId(id: number): string {
  return `${CONTENT_BLOCK_ID_PREFIX}${id}`;
}

export function contentBlockApiId(id: string): number | null {
  if (!id.startsWith(CONTENT_BLOCK_ID_PREFIX)) return null;
  const value = Number(id.slice(CONTENT_BLOCK_ID_PREFIX.length));
  return Number.isInteger(value) && value > 0 ? value : null;
}

function offeringReference(item: ContentBlockItem): OfferingReference | null {
  switch (item.refType) {
    case "BOOKING":
      return {kind: "service", id: item.refId};
    case "LIVE_EVENT":
      return {kind: "live-event", id: item.refId};
    case "PRODUCT":
      return {kind: "digital-product", id: item.refId};
    case "PRODUCT_SERVICE":
      return {kind: "product-service", id: item.refId};
  }
}

function richTextBlock(block: ContentBlock): RichTextBlock {
  return {
    id: contentBlockDocumentId(block.id),
    type: "richText",
    visible: true,
    content: [
      ...(block.title ? [{type: "heading" as const, text: block.title}] : []),
      {type: "paragraph", text: block.description ?? ""},
    ],
  };
}

function offeringBlock(block: ContentBlock): OfferingCollectionBlock {
  const isLiveEvent = block.type === "live_event";
  const isSession = block.type === "1:1_sessions";
  const offerings = [...block.items]
    .sort((left, right) => left.position - right.position)
    .map(offeringReference)
    .filter((reference): reference is OfferingReference => Boolean(reference));

  return {
    id: contentBlockDocumentId(block.id),
    type: "offeringCollection",
    visible: true,
    title: block.title ?? "",
    description: block.description,
    collection: isSession ? "services" : isLiveEvent ? "liveEvents" : "store",
    source: {mode: "selected", offerings},
    limit: Math.max(offerings.length, 1),
    layout: "grid",
    mobileColumns: 1,
    buttonLabel: isSession ? "Book now" : isLiveEvent ? "Reserve a spot" : "Buy now",
  };
}

export function contentBlockToDocumentBlock(block: ContentBlock): PublicProfileBlock {
  return block.type === "text" ? richTextBlock(block) : offeringBlock(block);
}

export function isContentBlockSupported(block: PublicProfileBlock): boolean {
  return (
    block.type === "richText" ||
    (block.type === "offeringCollection" &&
      (block.collection === "services" ||
        block.collection === "liveEvents" ||
        block.collection === "store"))
  );
}

function itemRequest(reference: OfferingReference, position: number) {
  if (reference.kind === "service") {
    return {refType: "BOOKING" as const, refId: reference.id, position};
  }
  if (reference.kind === "live-event") {
    return {refType: "LIVE_EVENT" as const, refId: reference.id, position};
  }
  if (reference.kind === "digital-product") {
    return {refType: "PRODUCT" as const, refId: reference.id, position};
  }
  if (reference.kind === "product-service") {
    return {refType: "PRODUCT_SERVICE" as const, refId: reference.id, position};
  }
  return null;
}

export function documentBlockToContentBlockRequest(
  block: PublicProfileBlock,
  position: number
): ContentBlockRequest | null {
  if (block.type === "richText") {
    const title = block.content.find((node) => node.type === "heading")?.text ?? "";
    const description = block.content
      .filter((node) => node.type !== "heading")
      .map((node) => node.text)
      .filter(Boolean)
      .join("\n");
    return {type: "text", title, description, position};
  }

  if (
    block.type !== "offeringCollection" ||
    (block.collection !== "services" &&
      block.collection !== "liveEvents" &&
      block.collection !== "store")
  ) {
    return null;
  }
  if (block.source.mode !== "selected") {
    throw new Error(
      `${block.title || "This block"} must use selected offerings before it can be saved.`
    );
  }

  const items = block.source.offerings
    .map(itemRequest)
    .filter((item): item is NonNullable<typeof item> => Boolean(item));
  if (items.length !== block.source.offerings.length) {
    throw new Error(`${block.title || "This block"} contains an unsupported offering.`);
  }

  return {
    type:
      block.collection === "services"
        ? "1:1_sessions"
        : block.collection === "liveEvents"
        ? "live_event"
        : "store",
    title: block.title,
    description: block.description,
    position,
    items,
  };
}

export function mergeContentBlocks(
  document: PublicProfileDocument,
  contentBlocks: ContentBlock[]
): PublicProfileDocument {
  const unsupportedBlocks = document.blocks.filter(
    (block) => !isContentBlockSupported(block)
  );
  const persistedBlocks = [...contentBlocks]
    .sort((left, right) => left.position - right.position)
    .map((contentBlock) => {
      const persisted = contentBlockToDocumentBlock(contentBlock);
      const existing = document.blocks.find((block) => block.id === persisted.id);
      if (persisted.type === "offeringCollection" && existing?.type === persisted.type) {
        return {
          ...persisted,
          visible: existing.visible,
          layout: existing.layout,
          mobileColumns: existing.mobileColumns,
          buttonLabel: existing.buttonLabel,
        };
      }
      return existing ? {...persisted, visible: existing.visible} : persisted;
    });
  return {...document, blocks: [...persistedBlocks, ...unsupportedBlocks]};
}

export async function getContentBlocks(): Promise<ContentBlock[]> {
  const response = await httpClientV2.get<ApiResponse<ContentBlock[]>>(
    "profile/content-blocks"
  );
  return response.data;
}

export async function createContentBlock(
  payload: ContentBlockRequest
): Promise<ContentBlock> {
  const response = await httpClientV2.post<ApiResponse<ContentBlock>>(
    "profile/content-blocks",
    payload
  );
  return response.data;
}

export async function updateContentBlock(
  id: number,
  payload: ContentBlockRequest
): Promise<ContentBlock> {
  const response = await httpClientV2.patch<ApiResponse<ContentBlock>>(
    `profile/content-blocks/${id}`,
    payload
  );
  return response.data;
}

export async function deleteContentBlock(id: number): Promise<void> {
  await httpClientV2.delete<ApiMutationResponse>(`profile/content-blocks/${id}`);
}
